import { useState } from "react";
import { comments } from "@/mocks/trending";

const toneStyles: Record<string, string> = {
  agree: "bg-secondary-100 text-secondary-800",
  banter: "bg-accent-100 text-accent-700",
  question: "bg-primary-100 text-primary-700",
};

const toneLabel: Record<string, string> = {
  agree: "认同围观",
  banter: "神评调侃",
  question: "理性盘逻辑",
};

export default function Community() {
  const [liked, setLiked] = useState<Record<string, boolean>>({});

  return (
    <section id="community" className="mx-auto w-full max-w-7xl px-4 md:px-6 py-12 md:py-16">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* 左：标题与热评 */}
        <div className="lg:col-span-7">
          <h2 className="flex items-center gap-2 text-2xl md:text-3xl font-black text-foreground-950">
            <span className="w-9 h-9 flex items-center justify-center rounded-xl bg-primary-500 text-background-50">
              <i className="ri-question-answer-fill"></i>
            </span>
            群众围观 · 评论区在吵什么
          </h2>
          <p className="mt-2 text-sm text-foreground-500">高赞神评、前排围观、理性盘逻辑，热点底下的真香现场。</p>

          <div className="mt-7 flex flex-col gap-4">
            {comments.map((c) => {
              const isLiked = !!liked[c.id];
              const curLikes = c.likes + (isLiked ? 1 : 0);
              return (
                <div
                  key={c.id}
                  className="rounded-2xl border border-background-200/80 bg-background-50 p-5 hover:border-primary-200 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="relative w-10 h-10 overflow-hidden rounded-full">
                      <img src={c.avatar} alt={c.author} className="absolute inset-0 h-full w-full object-cover" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-bold text-foreground-900">{c.author}</p>
                      <p className="text-xs text-foreground-400">{c.badge}</p>
                    </div>
                    <span className={`ml-auto shrink-0 rounded-full px-2.5 py-1 text-[11px] font-semibold ${toneStyles[c.tone]}`}>
                      {toneLabel[c.tone]}
                    </span>
                  </div>
                  <p className="mt-3 text-[15px] text-foreground-800 leading-relaxed">“{c.text}”</p>
                  <div className="mt-4 flex items-center gap-4">
                    <button
                      onClick={() => setLiked((prev) => ({ ...prev, [c.id]: !prev[c.id] }))}
                      className={`inline-flex cursor-pointer items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${
                        isLiked
                          ? "bg-primary-500 text-background-50"
                          : "bg-background-100 text-foreground-600 hover:text-primary-600"
                      }`}
                    >
                      <span className="w-3.5 h-3.5 flex items-center justify-center">
                        <i className={isLiked ? "ri-thumb-up-fill" : "ri-thumb-up-line"}></i>
                      </span>
                      {curLikes.toLocaleString()}
                    </button>
                    <span className="inline-flex items-center gap-1.5 text-xs text-foreground-500">
                      <span className="w-3.5 h-3.5 flex items-center justify-center">
                        <i className="ri-chat-1-line"></i>
                      </span>
                      {c.replies.toLocaleString()} 条回复
                    </span>
                    <button className="ml-auto cursor-pointer text-xs text-foreground-400 hover:text-primary-600">
                      展开回复
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* 右：围观侧栏 */}
        <div className="lg:col-span-5 flex flex-col gap-5">
          <div className="rounded-2xl bg-gradient-to-br from-primary-500 to-primary-700 p-6 text-background-50">
            <p className="flex items-center gap-2 text-sm font-bold">
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-megaphone-fill"></i>
              </span>
              前排征集 · 你的瓜点
            </p>
            <p className="mt-3 text-sm leading-relaxed text-background-50/90">
              有值得围观的新鲜话题？到讨论区甩上来，热度够了就帮你顶上热榜，让更多人看到。
            </p>
            <button className="mt-5 inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-full bg-background-50 px-5 py-2.5 text-sm font-bold text-primary-700 hover:bg-background-100 transition-colors">
              发布我的瓜点
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line"></i>
              </span>
            </button>
          </div>

          <div className="rounded-2xl border border-background-200/80 bg-background-50 p-6">
            <h3 className="flex items-center gap-2 text-base font-bold text-foreground-900">
              <span className="w-7 h-7 flex items-center justify-center rounded-lg bg-accent-500 text-background-50">
                <i className="ri-eye-fill"></i>
              </span>
              今日围观风向
            </h3>
            <div className="mt-4 space-y-3">
              <Stat label="正在围观的话题" value="23,847" diff="+12.4%" up />
              <Stat label="新增讨论声量" value="861 万" diff="+8.1%" up />
              <Stat label="前排占座观众" value="31,204" diff="+6.7%" up />
            </div>
            <div className="mt-5 rounded-xl bg-background-100 p-4">
              <p className="text-xs font-semibold text-foreground-700">风向一句话</p>
              <p className="mt-1.5 text-sm text-foreground-600 leading-relaxed">
                今天「群众围观」氛围比昨天更旺，理性盘逻辑与神评调侃各占一半，稳稳跟榜别掉队。
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Stat({ label, value, diff, up }: { label: string; value: string; diff: string; up?: boolean }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-sm text-foreground-500">{label}</span>
      <span className="flex items-center gap-2">
        <span className="font-display text-xl text-foreground-900">{value}</span>
        <span className={`text-xs font-semibold ${up ? "text-primary-600" : "text-secondary-600"}`}>{diff}</span>
      </span>
    </div>
  );
}